<template>
  <div class="page-container notice-list-page">
    <div class="page-header">
      <div class="container">
        <h1>{{ $t('pages.notice_list_title') }}</h1>
        <p class="breadcrumb">
          <router-link to="/">{{ $t('index.latest_news') }}</router-link> /
          <span>{{ $t('pages.notice_list_title') }}</span>
        </p>
      </div>
    </div>

    <div class="page-content container">
      <el-row :gutter="30">
        <!-- 左侧：公告列表 -->
        <el-col :span="18" :xs="24">
          <div class="list-wrapper">
            <div v-if="loading" class="loading-placeholder">
              <el-skeleton :rows="6" animated />
            </div>
            <div v-else-if="noticeList.length === 0" class="empty-data">
              <el-empty :description="$t('common.no_data')" />
            </div>
            <div v-else>
              <div v-for="item in noticeList" :key="item.id" class="notice-item">
                <div class="item-icon">
                  <el-icon><Bell /></el-icon>
                </div>
                <div class="item-content">
                  <router-link :to="`/notice/${item.id}`">
                    <h3>{{ item.title }}</h3>
                  </router-link>
                  <p class="summary">{{ item.summary }}</p>
                  <div class="meta">
                    <span class="category">{{ item.category.name }}</span>
                    <span class="time">{{ $t('pages.publish_time') }}: {{ formatTime(item.publish_time) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- 分页：适配新的 count/current 格式 -->
          <div v-if="!loading && totalPages > 1" class="pagination-wrapper">
            <el-pagination
              background
              layout="prev, pager, next"
              :total="totalCount"
              :page-size="pageSize"
              :current-page="currentPage"
              :page-count="totalPages"
              @current-change="handlePageChange"
            />
          </div>
        </el-col>

        <!-- 右侧：侧边栏（分类） -->
        <el-col :span="6" :xs="24" class="sidebar-col">
          <div class="sidebar">
            <div class="sidebar-block">
              <h4>{{ $t('pages.notice_list_title') }}分类</h4>
              <div v-if="categoryLoading">
                <el-skeleton :rows="5" animated />
              </div>
              <ul v-else>
                <!-- 全部分类选项 -->
                <li :class="{ active: currentCategory === null }">
                  <a href="#" @click.prevent="selectCategory(null)">{{ $t('common.all_notice') }}</a>
                </li>
                <!-- 接口加载的分类 -->
                <li
                  v-for="cat in flatCategories"
                  :key="cat.id"
                  :class="{ active: cat.id === currentCategory }"
                >
                  <a href="#" @click.prevent="selectCategory(cat.id)">{{ cat.name }}</a>
                </li>
              </ul>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format } from 'date-fns';
import { Bell } from '@element-plus/icons-vue';
import { getNoticeListApi, getNoticeCategoryApi } from '@/assets/api/website';

// -------------------------- 类型定义 --------------------------
/** 分类项类型 */
interface CategoryItem {
  id: number;
  name: string;
  pid: number;
  level: number;
  sort: number;
  children?: CategoryItem[];
}

/** 公告分类下的子项类型 */
interface NoticeCategory {
  id: number;
  name: string;
}

/** 公告列表项类型 */
interface NoticeListItem {
  id: number;
  title: string;
  summary?: string;
  publish_time: string | Date;
  category: NoticeCategory;
}

/** 公告列表接口返回类型 - 新版：count/current 格式 */
interface NoticeListResponse {
  list: NoticeListItem[];
  count: number;  // 总页数
  current: number; // 当前页码
}

// -------------------------- 状态管理 --------------------------
const { t, locale } = useI18n();

const loading = ref(true);
const categoryLoading = ref(true);

// 公告列表数据
const noticeList = ref<NoticeListItem[]>([]);
// 分页相关
const pageSize = ref(10);
const currentPage = ref(1);
const totalPages = ref(0); // 总页数
const totalCount = ref(0); // 总条数（通过总页数 * 每页大小估算）
const currentCategory = ref<number | null>(null);

// 分类数据
const categories = ref<CategoryItem[]>([]);
// 扁平化的分类列表（处理树形结构）
const flatCategories = computed(() => {
  const flatten = (list: CategoryItem[]): CategoryItem[] => {
    return list.reduce((acc, item) => {
      acc.push(item);
      if (item.children && item.children.length) {
        acc.push(...flatten(item.children));
      }
      return acc;
    }, [] as CategoryItem[]);
  };
  return flatten(categories.value);
});

// -------------------------- 方法定义 --------------------------
/** 格式化时间 */
const formatTime = (time: string | Date) => {
  if (!time) return '';
  try {
    return format(new Date(time), 'yyyy-MM-dd HH:mm');
  } catch (e) {
    return time.toString();
  }
};

/** 获取公告分类 */
const fetchCategories = async () => {
  categoryLoading.value = true;
  try {
    // 注意：这里需要调用公告的分类接口，与新闻的可能不同
    const res = await getNoticeCategoryApi({ lang: locale.value });
    console.log('公告分类数据:', res);
    categories.value = res as CategoryItem[];
  } catch (error) {
    console.error('获取公告分类失败:', error);
    categories.value = [];
  } finally {
    categoryLoading.value = false;
  }
};

/** 获取公告列表 */
const fetchNoticeList = async () => {
  loading.value = true;
  try {
    const res = await getNoticeListApi({
      lang: locale.value,
      page: currentPage.value,
      size: pageSize.value,
      category_id: currentCategory.value
    }) as NoticeListResponse;

    console.log('公告列表数据:', res);

    noticeList.value = res.list;
    totalPages.value = res.count; // 总页数
    currentPage.value = res.current; // 当前页码

    // 计算总条数（用于分页组件显示）
    totalCount.value = totalPages.value * pageSize.value;

  } catch (error) {
    console.error('获取公告列表失败:', error);
    noticeList.value = [];
    totalPages.value = 0;
    totalCount.value = 0;
  } finally {
    loading.value = false;
  }
};

/** 分页切换 */
const handlePageChange = (page: number) => {
  currentPage.value = page;
  fetchNoticeList();
  // 滚动到顶部
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

/** 分类选择 */
const selectCategory = (categoryId: number | null) => {
  currentCategory.value = categoryId;
  currentPage.value = 1; // 切换分类重置页码
  fetchNoticeList();
};

// -------------------------- 计算属性 --------------------------
// 是否需要显示分页
const showPagination = computed(() => {
  return !loading.value && totalPages.value > 1;
});

// -------------------------- 监听与初始化 --------------------------
// 监听语言变化，重新加载数据
watch(
  () => locale.value,
  () => {
    fetchCategories();
    fetchNoticeList();
  },
  { immediate: true }
);

// 初始化
// onMounted(() => {
//   fetchCategories();
//   fetchNoticeList();
// });
</script>

<style scoped lang="scss">
@import '@/assets/css/common.scss';

.notice-list-page {
  .page-header {
    background-color: #f5f5f5;
    padding: 100px 0 40px;

    h1 {
      color: $primary-color;
      margin-bottom: 10px;
    }

    .breadcrumb {
      font-size: 14px;
      color: #999;

      a {
        color: #666;
        text-decoration: none;

        &:hover {
          color: $primary-color;
        }
      }

      span {
        color: $primary-color;
      }
    }
  }

  .page-content {
    padding: 50px;
  }

  .list-wrapper {
    min-height: 500px;
  }

  .notice-item {
    display: flex;
    align-items: flex-start;
    margin-bottom: 20px;
    padding: 15px;
    border-bottom: 1px dashed #ddd;
    transition: background-color 0.3s, transform 0.3s;
    border-radius: 8px;

    &:hover {
      background-color: #f9f9f9;
      transform: translateX(5px);
    }

    .item-icon {
      flex-shrink: 0;
      font-size: 24px;
      color: $primary-color;
      margin-right: 15px;
      margin-top: 5px;
    }

    .item-content {
      flex-grow: 1;

      h3 {
        font-size: 18px;
        margin: 0 0 8px 0;
        color: #333;
        transition: color 0.3s;

        &:hover {
          color: $primary-color;
        }
      }

      .summary {
        color: #666;
        font-size: 14px;
        margin-bottom: 8px;
        line-height: 1.5;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .meta {
        display: flex;
        flex-wrap: wrap;
        gap: 15px;
        font-size: 13px;
        color: #999;

        .category {
          color: $primary-color;
          font-weight: bold;
          background: rgba($primary-color, 0.1);
          padding: 2px 8px;
          border-radius: 4px;
        }

        .time {
          display: flex;
          align-items: center;

          &::before {
            content: "⏱️";
            margin-right: 4px;
          }
        }
      }
    }
  }

  .pagination-wrapper {
    text-align: center;
    margin-top: 30px;
  }

  .sidebar {
    padding: 20px;
    background-color: #f9f9f9;
    border-radius: 8px;

    .sidebar-block {
      margin-bottom: 30px;

      h4 {
        font-size: 18px;
        color: $primary-color;
        border-bottom: 2px solid $primary-color;
        padding-bottom: 10px;
        margin-bottom: 15px;
      }

      ul {
        list-style: none;
        padding: 0;

        li {
          margin-bottom: 10px;
          font-size: 14px;

          a {
            color: #666;
            text-decoration: none;
            display: block;
            padding: 5px 0;
            transition: color 0.3s, padding-left 0.3s;

            &:hover {
              color: $primary-color;
              padding-left: 8px;
            }
          }

          &.active a {
            color: $primary-color;
            font-weight: bold;
            border-left: 3px solid $primary-color;
            padding-left: 8px;
          }
        }
      }
    }
  }

  @media (max-width: 768px) {
    .notice-item {
      padding: 12px;

      .item-icon {
        font-size: 20px;
        margin-right: 12px;
      }

      .item-content {
        h3 {
          font-size: 16px;
        }

        .meta {
          flex-direction: column;
          gap: 5px;
        }
      }
    }

    .sidebar-col {
      margin-top: 30px;
    }
  }
}
</style>